"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAiLoginGate } from "@/components/ai-login-gate";
import { useAuth } from "@/components/auth-provider";
import { secureGet } from "@/lib/security/fetch-client";
import {
  getAgentCore,
  stopAgentTask,
  onQuotaChange,
  AgentQuotaExhaustedError,
} from "@/lib/agent/client";
import { AgentMessageList, type Turn, type HistoryEvent } from "./agent-message";
import { AgentActivityView, type AgentActivity } from "./agent-activity";
import { AgentEmptyState } from "./agent-empty-state";
import { Bot, Loader2, Send, Sparkles, X } from "lucide-react";
import { trackProductEvent } from "@/lib/analytics-events";

/**
 * 右下角 AI 助手悬浮入口（未展开态）。
 * 设计约束：未展开不显示剩余次数、占用面积精简（移动端优先）、一眼能识别是 AI 助手。
 * - 纯圆形 FAB，移动端/桌面统一紧凑尺寸，不横向铺开。
 * - Bot 机器人图标为主体 + Sparkles 角标暗示 AI 智能体。
 * - 运行中：图标转 spinner；额度耗尽：右上角红点提示（不显示数字）。
 * 剩余次数仅在展开后的面板头部展示。
 */

const QQ_GROUP_URL = "https://jq.qq.com/?_wv=1027&k=nZIcjTG7";

type AgentStatus = "idle" | "running" | "completed" | "error" | "stopped";

interface AgentLike extends EventTarget {
  status: AgentStatus;
  history: HistoryEvent[];
  execute(task: string): Promise<{ success: boolean; data: string }>;
  stop(): Promise<void>;
}

export function AgentPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [activeTurnIndex, setActiveTurnIndex] = useState<number | null>(null);
  const [status, setStatus] = useState<AgentStatus>("idle");
  const [activity, setActivity] = useState<AgentActivity | null>(null);
  const [input, setInput] = useState("");
  const [quota, setQuota] = useState({ used: 0, remaining: 50, limit: 50 });
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [agentReady, setAgentReady] = useState(false);

  const agentRef = useRef<AgentLike | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { requireLogin, dialog: loginDialog } = useAiLoginGate();
  const { user } = useAuth();

  const running = status === "running";

  // 登录后拉取配额并订阅变化。
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    secureGet<{ used: number; remaining: number; limit: number }>("/api/agent/quota")
      .then((q) => {
        if (cancelled) return;
        setQuota(q);
        setQuotaExceeded(q.remaining <= 0);
      })
      .catch(() => {});
    const off = onQuotaChange((remaining) => {
      if (cancelled) return;
      setQuota((prev) => ({ ...prev, remaining }));
      setQuotaExceeded(remaining <= 0);
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [user]);

  // 面板打开且登录后懒加载 agent 并订阅事件。
  useEffect(() => {
    if (!isOpen || !user || agentRef.current) return;
    let disposed = false;
    let agent: AgentLike | null = null;

    const onStatus = () => setStatus(agent?.status ?? "idle");
    const onHistory = () => {
      if (!agent) return;
      const events = [...agent.history];
      setTurns((prev) => {
        if (activeTurnIndexRef.current === null) return prev;
        const next = [...prev];
        next[activeTurnIndexRef.current] = { ...next[activeTurnIndexRef.current], events };
        return next;
      });
    };
    const onActivity = (e: Event) => {
      setActivity((e as CustomEvent<AgentActivity>).detail);
    };

    getAgentCore()
      .then((a) => {
        if (disposed) return;
        agent = a as unknown as AgentLike;
        agentRef.current = agent;
        agent.addEventListener("statuschange", onStatus);
        agent.addEventListener("historychange", onHistory);
        agent.addEventListener("activity", onActivity as EventListener);
        setAgentReady(true);
      })
      .catch(() => {});

    return () => {
      disposed = true;
      agent?.removeEventListener("statuschange", onStatus);
      agent?.removeEventListener("historychange", onHistory);
      agent?.removeEventListener("activity", onActivity as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // activeTurnIndex 用 ref 供事件回调读取最新值。
  const activeTurnIndexRef = useRef<number | null>(null);
  useEffect(() => {
    activeTurnIndexRef.current = activeTurnIndex;
  }, [activeTurnIndex]);

  // 自动滚动到底部。
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, activity]);

  const handleSend = useCallback(async (taskOverride?: string) => {
    const task = (taskOverride ?? input).trim();
    if (!task || running) return;
    if (!user) {
      requireLogin(() => setIsOpen(true));
      return;
    }
    if (quotaExceeded) return;
    const agent = agentRef.current;
    if (!agent || !agentReady) return;

    const turnIndex = turns.length;
    setTurns((prev) => [...prev, { userText: task, events: [] }]);
    setActiveTurnIndex(turnIndex);
    setInput("");
    setActivity(null);
    setStatus("running");
    trackProductEvent("ai_task_start", {
      ui_surface: "agent_panel",
      auth_state: "signed_in",
      feature_name: "page_agent",
    });

    try {
      const result = await agent.execute(task);
      trackProductEvent(result.success ? "ai_task_success" : "ai_task_failure", {
        ui_surface: "agent_panel",
        auth_state: "signed_in",
        feature_name: "page_agent",
        result_status: result.success ? "success" : "failure",
      });
    } catch (error) {
      if (error instanceof AgentQuotaExhaustedError) {
        trackProductEvent("ai_quota_exhausted", {
          ui_surface: "agent_panel",
          auth_state: "signed_in",
          feature_name: "page_agent",
        });
        setQuotaExceeded(true);
        setQuota((prev) => ({ ...prev, remaining: 0 }));
        setTurns((prev) => {
          const next = [...prev];
          next[turnIndex] = {
            ...next[turnIndex],
            events: [...next[turnIndex].events, { type: "error", message: "今日 50 次对话已用完，加入QQ群继续使用。" }],
          };
          return next;
        });
      } else {
        trackProductEvent("ai_task_failure", {
          ui_surface: "agent_panel",
          auth_state: "signed_in",
          feature_name: "page_agent",
          result_status: "error",
          error_type: error instanceof Error ? error.name : "unknown",
        });
        setTurns((prev) => {
          const next = [...prev];
          next[turnIndex] = {
            ...next[turnIndex],
            events: [
              ...next[turnIndex].events,
              { type: "error", message: error instanceof Error ? error.message : "任务执行失败" },
            ],
          };
          return next;
        });
      }
    } finally {
      setActivity(null);
      setActiveTurnIndex(null);
    }
  }, [agentReady, input, running, user, quotaExceeded, requireLogin, turns]);

  const handleStop = useCallback(async () => {
    await stopAgentTask();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleOpen = () => {
    if (!user) {
      requireLogin(() => setIsOpen(true));
      return;
    }
    if (!isOpen) {
      trackProductEvent("ai_panel_open", {
        ui_surface: "floating_button",
        auth_state: "signed_in",
        feature_name: "page_agent",
      });
    }
    setIsOpen((v) => !v);
  };

  return (
    <>
      {/* 右下角 AI 助手入口（未展开：紧凑圆形，不显示次数）。
          面板展开时隐藏悬浮球——面板自带关闭按钮，且桌面端面板与悬浮球同处右下角，
          不隐藏会导致悬浮球遮挡对话框。 */}
      {!(isOpen && user) && (
      <button
        type="button"
        onClick={handleOpen}
        aria-label="AI 热搜助手"
        title="AI 热搜助手"
        className="group fixed bottom-5 right-5 z-[2147483642] flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-lg shadow-sky-500/30 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sky-500/40 active:scale-95 sm:h-14 sm:w-14"
      >
        {/* 光晕脉动：吸引用户注意这是 AI 入口 */}
        <span className="pointer-events-none absolute inset-0 rounded-full bg-sky-400/40 opacity-0 transition-opacity group-hover:opacity-100 group-hover:animate-ping" aria-hidden="true" />
        {running ? (
          <Loader2 className="relative h-5 w-5 animate-spin" />
        ) : (
          <Bot className="relative h-5 w-5" />
        )}
        {user && quotaExceeded ? (
          // 额度耗尽红点提示（不显示具体数字，与 AI 角标互斥）
          <span
            className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-rose-500 ring-2 ring-white"
            aria-label="今日额度已用完"
          />
        ) : (
          // AI 角标：Sparkles 暗示智能体
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-sky-500/20">
            <Sparkles className="h-2.5 w-2.5 text-sky-500" />
          </span>
        )}
      </button>
      )}

      {/* 浮动面板：无全屏遮罩、无 backdrop-blur，页面其余区域可交互 */}
      {isOpen && user && (
        <section
          className="agent-chat-panel fixed bottom-24 right-5 z-[2147483642] flex max-h-[70vh] w-[min(92vw,420px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950 sm:bottom-5 sm:right-5"
          // 移动端：贴近全屏底部 sheet
          style={{ maxHeight: "70vh" }}
        >
          {/* 头部 */}
          <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 text-white">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">热搜 Agent</div>
                <div className="text-[11px] text-slate-400 dark:text-slate-500">
                  {user ? `今日剩余 ${quota.remaining} 次` : "登录后使用"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {running && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleStop()}
                  className="h-8 text-xs text-slate-500 hover:text-rose-500"
                >
                  停止
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-400 hover:text-slate-600"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </header>

          {/* 消息区 */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
            {turns.length === 0 ? (
              <AgentEmptyState
                disabled={!agentReady || running}
                onPick={(prompt, position) => {
                  trackProductEvent("agent_prompt_pick", {
                    ui_surface: "agent_empty_state",
                    auth_state: "signed_in",
                    feature_name: "page_agent",
                    result_position: position,
                  });
                  void handleSend(prompt);
                }}
              />
            ) : (
              <>
                <AgentMessageList turns={turns} />
                {running && activity && (
                  <div className="mt-3">
                    <AgentActivityView activity={activity} />
                  </div>
                )}
                {running && !activity && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>正在处理…</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 额度耗尽提示 */}
          {quotaExceeded && (
            <div className="mx-4 mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              今日 50 次已用完，次日 0 点恢复。
              <a href={QQ_GROUP_URL} target="_blank" rel="noreferrer" className="ml-1 font-medium text-amber-700 underline dark:text-amber-300">
                加入QQ群
              </a>
            </div>
          )}

          {/* 输入区 */}
          <footer className="border-t border-slate-100 p-3 dark:border-slate-800">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={running || quotaExceeded}
                rows={1}
                placeholder={quotaExceeded ? "今日额度已用完" : !agentReady ? "助手加载中…" : "输入任务，如：查看今天的热搜"}
                className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
              <Button
                size="icon"
                onClick={() => void (running ? handleStop() : handleSend())}
                disabled={!running && (!input.trim() || quotaExceeded || !agentReady)}
                aria-label={running ? "停止" : "发送"}
                className="h-11 w-11 shrink-0 rounded-xl bg-sky-500 hover:bg-sky-600"
              >
                {running ? <X className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </footer>
        </section>
      )}

      {loginDialog}
    </>
  );
}
